import { useState } from 'react';
import { getUsersFromOrganizations } from '@hotjar/user-resources';
import { fromUnixTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { Site } from '@hotjar/site';
import { useAccountSites } from '@hotjar/site';
import { useAccountTrialInfo, useOwnedAccount } from '@hotjar/account';
import {
  useEffectOnceWithDependencies,
  useUserResources,
} from '../../../external-dependencies/webapp-utils';
import type { SurveyModel } from '../api/survey-model';
import { createAnswerFromQuestionText } from '../utils/churn-answers';
import type { Answer } from '../utils/churn-answers';

// Get unique industries as string separated by comma
function getIndustries(sites: Site[]): string {
  return [...new Set(sites.map((site) => site.industry))].join(', ');
}

/**
 * Prepares answers needed to submit churn survey. Those answers come from sources like account sites and account info.
 */
export function useChurnSurveyAnswers({
  accountId,
  survey,
  subscriptionSubtotal,
}: {
  accountId: string;
  survey: SurveyModel | undefined;
  subscriptionSubtotal: string;
}): {
  implicitAnswers?: Record<string, Answer>;
} {
  const [implicitAnswers, setImplicitAnswers] = useState<Record<string, Answer> | undefined>();
  const {
    query: { data: sitesData, isFetched: isFetchedSites },
  } = useAccountSites(Number(accountId));
  const { data: userResourceData } = useUserResources();
  const accounTrialInfo = useAccountTrialInfo();
  const { data: accountData } = useOwnedAccount();

  useEffectOnceWithDependencies(
    () => {
      function addAnswers(question: string, answer: string) {
        if (survey !== undefined) {
          setImplicitAnswers((currentAnswers) => ({
            ...currentAnswers,
            [question]: createAnswerFromQuestionText(question, answer, survey),
          }));
        }
      }

      addAnswers('Account ID', accountId);

      if (accounTrialInfo !== null) {
        addAnswers(
          'Was paid customer prior to downgrade',
          accounTrialInfo.activeOrEligible ? 'No' : 'Yes',
        );
      }

      if (sitesData !== undefined) {
        addAnswers('Number of sites', sitesData.length.toString());
        addAnswers('Industry', getIndustries(sitesData));
      }

      if (userResourceData !== undefined) {
        const numberOfUsers = getUsersFromOrganizations(userResourceData.organizations).length;
        addAnswers('Number of users', numberOfUsers.toString());
      }

      if (accountData !== undefined) {
        const TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss'; // format: 2022-08-26 14:13:43
        const createdTime = formatInTimeZone(fromUnixTime(accountData.created), 'UTC', TIME_FORMAT);
        addAnswers('Highest plan', accountData.highestPlan);
        addAnswers('Account creation date', createdTime);
        addAnswers('Account billing cycle', accountData.billingCycle);
      }
      addAnswers('Plan Rate', subscriptionSubtotal);
    },
    [isFetchedSites, userResourceData, accounTrialInfo, accountData, survey],
    () =>
      isFetchedSites &&
      userResourceData !== undefined &&
      accounTrialInfo !== null &&
      accountData !== null &&
      survey !== undefined,
  );

  return { implicitAnswers };
}
